<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Pts2ControllerSelector from '../components/pts2/Pts2ControllerSelector.vue'
import Pts2IngressDiagnostics from '../components/pts2/Pts2IngressDiagnostics.vue'
import { getNetworkControllerSummaries } from '../pts2/controllers.js'
import { session } from '../session.js'
import { useI18n } from '../i18n.js'
import '../pts2/controllers.css'

const { t } = useI18n()
const route = useRoute(), router = useRouter()
const controllers = ref([]), loading = ref(true), error = ref(''), nextCursor = ref(null)
const selectedId = ref(null)
const selected = computed(() => controllers.value.find((item) => item.id === selectedId.value))
let request, disposed = false
async function load(more = false) {
  request?.abort()
  const active = new AbortController()
  request = active
  loading.value = true
  error.value = ''
  try {
    const page = await getNetworkControllerSummaries({ limit: 100, cursor: more ? nextCursor.value : null, signal: active.signal })
    if (disposed || active.signal.aborted) return
    controllers.value = more ? [...controllers.value, ...page.items] : page.items
    nextCursor.value = page.nextCursor
    const requested = Number(route.query.controllerId) || session.pts2Context?.activeControllerId
    if (!selectedId.value && controllers.value.some((item) => item.id === requested)) selectedId.value = requested
    if (!selectedId.value && controllers.value.length === 1 && !nextCursor.value) selectedId.value = controllers.value[0].id
    if (selectedId.value && !selected.value) selectedId.value = null
  } catch (failure) {
    if (!active.signal.aborted && !disposed) { controllers.value = []; selectedId.value = null; error.value = t('pts2UiDataRefreshError') }
  } finally { if (!disposed && !active.signal.aborted) loading.value = false }
}
function select(id) {
  selectedId.value = id
  router.replace({ name: 'pts2-diagnostics', query: { controllerId: String(id) } })
}
onMounted(() => load())
onBeforeUnmount(() => { disposed = true; request?.abort() })
</script>

<template>
  <div class="pc-page">
    <header class="pc-heading"><h1 id="diagnostics-page-title">{{ t('pts2DiagTitle') }}</h1></header>
    <Pts2ControllerSelector :controllers="controllers" :active-controller-id="selectedId" :disabled="loading" @select="select" />
    <p v-if="error" class="pc-notice" role="alert">{{ error }} <button @click="load()">{{ t('pts2UiRetry') }}</button></p>
    <p v-else-if="loading" role="status">{{ t('loading') }}</p>
    <button v-if="nextCursor" class="pc-secondary" :disabled="loading" @click="load(true)">{{ t('pts2UiLoadMore') }}</button>
    <template v-if="selected">
      <p>{{ selected.displayName }} · {{ selected.stationDisplayName }}</p>
      <Pts2IngressDiagnostics :key="selected.id" :station-id="selected.stationId" :controller-id="selected.id" />
    </template>
    <p v-else-if="!loading && !error" class="pc-notice">{{ controllers.length ? t('pts2UiChooseController') : t('pts2UiControllersUnavailable') }}</p>
  </div>
</template>
